import { ReactNode, useMemo } from 'react';
import { CallObject } from '@weave/schema-gen-ts/dist/schemas/phone/callroute/v1/callroute_service.pb';
import { ReactFlowProvider, Node as ReactFlowNode, NodeProps } from '@xyflow/react';
import '@xyflow/react/dist/style.css';
import { CallRouteV1 } from '@frontend/api-call-route';
import { FlowContent, BasicNode } from '@frontend/node-flow';
import { CustomNodeProps, DataPayload, extendableNodes, SharedNodeProps } from './data';
import { FallbackNode } from './nodes/fallback-node';
import { HoursTypeNode } from './nodes/hours-type-node';
import {
  VoicemailNode,
  CallGroupNode,
  PlayNode,
  ForwardingDeviceNode,
  ForwardingNumberNode,
  PhoneTreeNode,
  OfficeHoursNode,
  CallQueueNode,
  RepeatNode,
  CallRouteNode,
} from './nodes/instruction-nodes';
import { StartNode } from './nodes/start-node';
import { TreeOptionNode } from './nodes/tree-option-node';

// It’s important that the nodeTypes are memoized or defined outside of the component.
// Otherwise React creates a new object on every render which leads to performance issues and bugs.
const flowNodeTypes: (
  commonProps: CustomNodeProps
) => Record<
  CallRouteV1.Types.NodeType,
  (props: NodeProps<ReactFlowNode<DataPayload, CallRouteV1.Types.NodeType>>) => ReactNode
> = (commonProps) => ({
  [CallRouteV1.Types.NodeType.NODE_TYPE_START]: StartNode,
  [CallRouteV1.Types.NodeType.NODE_TYPE_CALL_GROUP]: ({ ...props }) => <CallGroupNode {...props} {...commonProps} />,
  [CallRouteV1.Types.NodeType.NODE_TYPE_CALL_ROUTE]: ({ ...props }) => <CallRouteNode {...props} {...commonProps} />,
  [CallRouteV1.Types.NodeType.NODE_TYPE_PHONE_HOURS]: ({ ...props }) => <OfficeHoursNode {...props} {...commonProps} />,
  [CallRouteV1.Types.NodeType.NODE_TYPE_OPEN_HOURS]: ({ ...props }) => <HoursTypeNode {...props} {...commonProps} />,
  [CallRouteV1.Types.NodeType.NODE_TYPE_CLOSED_HOURS]: ({ ...props }) => <HoursTypeNode {...props} {...commonProps} />,
  [CallRouteV1.Types.NodeType.NODE_TYPE_BREAK_HOURS]: ({ ...props }) => <HoursTypeNode {...props} {...commonProps} />,
  [CallRouteV1.Types.NodeType.NODE_TYPE_PLAY_MESSAGE]: ({ ...props }) => <PlayNode {...props} {...commonProps} />,
  [CallRouteV1.Types.NodeType.NODE_TYPE_PHONE_TREE]: ({ ...props }) => <PhoneTreeNode {...props} {...commonProps} />,
  [CallRouteV1.Types.NodeType.NODE_TYPE_TREE_OPTION]: ({ ...props }) => <TreeOptionNode {...props} {...commonProps} />,
  [CallRouteV1.Types.NodeType.NODE_TYPE_FORWARD_DEVICE]: ({ ...props }) => (
    <ForwardingDeviceNode {...props} {...commonProps} />
  ),
  [CallRouteV1.Types.NodeType.NODE_TYPE_FORWARD_NUMBER]: ({ ...props }) => (
    <ForwardingNumberNode {...props} {...commonProps} />
  ),
  [CallRouteV1.Types.NodeType.NODE_TYPE_VOICEMAIL_BOX]: ({ ...props }) => <VoicemailNode {...props} {...commonProps} />,
  [CallRouteV1.Types.NodeType.NODE_TYPE_CALL_QUEUE]: ({ ...props }) => <CallQueueNode {...props} {...commonProps} />,
  [CallRouteV1.Types.NodeType.NODE_TYPE_REPEAT]: ({ ...props }) => <RepeatNode {...props} {...commonProps} />,
  [CallRouteV1.Types.NodeType.NODE_TYPE_FALLBACK]: ({ ...props }) => <FallbackNode {...props} {...commonProps} />,
  [CallRouteV1.Types.NodeType.NODE_TYPE_UNSPECIFIED]: () => null,
  [CallRouteV1.Types.NodeType.NODE_TYPE_BOOLEAN]: ({ ...props }) => <BasicNode {...props} type='basic' />,
  [CallRouteV1.Types.NodeType.NODE_TYPE_TERMINATE]: ({ ...props }) => <BasicNode {...props} type='basic' />,
});

type ExtendedNode = CallRouteV1.Types.Node & Pick<DataPayload, 'isExtendable' | 'isPhoneTreeDescendent' | 'warning'>;

export function CallRouteFlow({
  nodes,
  edges,
  canEdit = false,
  showDebugButtons = false,
  onCallObjectLinkClick,
  onEditIconClick,
  onPlusIconClick,
  onDeleteIconClick,
  AudioPlayerComponent,
  DayHoursListComponent,
  initialCallObjectId,
}: {
  nodes: ExtendedNode[];
  edges: { sourceId: string; targetId: string; label?: string; type?: string }[];
  canEdit?: boolean;
  showDebugButtons?: boolean;
  initialCallObjectId?: string;
  onPlusIconClick?: (data: { parentNodeId: string }) => void;
  onCallObjectLinkClick?: SharedNodeProps['onCallObjectLinkClick'];
  onEditIconClick?: SharedNodeProps['onEditIconClick'];
  onDeleteIconClick?: SharedNodeProps['onDeleteIconClick'];
  AudioPlayerComponent: SharedNodeProps['AudioPlayerComponent'];
  DayHoursListComponent: SharedNodeProps['DayHoursListComponent'];
}) {
  const commonProps: CustomNodeProps = useMemo(
    () => ({
      canEdit,
      onCallObjectLinkClick,
      onEditIconClick,
      onDeleteIconClick,
      AudioPlayerComponent,
      DayHoursListComponent,
    }),
    [AudioPlayerComponent, DayHoursListComponent]
  );
  const memoizedFlowNodeTypes = useMemo(() => flowNodeTypes(commonProps), [commonProps]);

  const memoizedNodes: ReactFlowNode<DataPayload, CallRouteV1.Types.NodeType | 'basic'>[] = useMemo(
    () =>
      nodes.map((node) => {
        return {
          id: node.id,
          type: Object.values(CallRouteV1.Types.NodeType).includes(node.type) ? node.type : 'basic',
          // Need to set a default position for the node since it is required by React Flow
          position: { x: -100, y: -100 },
          data: {
            id: node.id,
            label: node.callObject.primitiveName,
            warning: node.warning || '',
            callObject: node.callObject as CallObject,
            isPhoneTreeDescendent: node.isPhoneTreeDescendent,
            isExtendable: node.isExtendable,
          },
        };
      }),
    [nodes]
  );

  const memoizedEdges = useMemo(
    () =>
      edges.map((edge) => {
        const sourceNode = nodes.find((node) => node.id === edge.sourceId);

        return {
          id: edge.sourceId + ':' + edge.targetId,
          source: edge.sourceId,
          target: edge.targetId,
          type: edge.type ?? 'basic',
          selectable: false, // make the edges unselectable so that you can't drag them
          data: {
            isExtendable: sourceNode?.type
              ? extendableNodes.includes(sourceNode.type as CallRouteV1.Types.NodeType)
              : false,
            label: edge.label,
          },
        };
      }),
    [edges]
  );

  // The default array of nodes to show when no nodes are provided, it will show a start node.
  const defaultEmptyNodes: ReactFlowNode<DataPayload, CallRouteV1.Types.NodeType>[] = useMemo(
    () => [
      {
        id: 'start',
        type: CallRouteV1.Types.NodeType.NODE_TYPE_START,
        // Need to set a default position for the node since it is required by React Flow
        position: { x: -100, y: -100 },
        data: {
          id: 'start',
          label: 'Incoming Call',
          warning: '',
          isExtendable: true,
          isPhoneTreeDescendent: false,
          callObject: {
            primitiveName: 'Incoming Call',
            primitiveId: '',
            instructionId: '',
            instructionSetId: '',
          } as CallObject,
        },
      },
    ],
    []
  );

  return (
    <div style={{ width: '100%', height: '100%' }}>
      <ReactFlowProvider>
        <FlowContent<CallRouteV1.Types.NodeType, DataPayload>
          nodes={
            !!nodes.length ? memoizedNodes : defaultEmptyNodes // Use default empty nodes if no nodes are provided
          }
          edges={memoizedEdges}
          canEdit={canEdit}
          customNodeTypes={memoizedFlowNodeTypes}
          onPlusIconClick={onPlusIconClick}
          showDebugButtons={showDebugButtons}
          initialNodeId={initialCallObjectId}
        />
      </ReactFlowProvider>
    </div>
  );
}
